import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, DollarSign, Receipt, TrendingUp, TrendingDown, Wallet, Users, Calendar, MapPin, Eye, Download, Mail, FileText, Check, Notebook } from 'lucide-react';

const EvenementDetail = () => {
  const { id } = useParams();
  const [evenement, setEvenement] = useState(null);
  const [note, setNote] = useState('');
  const [rushesDate, setRushesDate] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [roadmap, setRoadmap] = useState(null);
  const [allPrestataires, setAllPrestataires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paiementModalOpen, setPaiementModalOpen] = useState(false);
  const [depenseModalOpen, setDepenseModalOpen] = useState(false);
  const [editDepenseModalOpen, setEditDepenseModalOpen] = useState(false);
  const [editingDepense, setEditingDepense] = useState(null);
  const [paiementForm, setPaiementForm] = useState({ date: '', montant: '', type: 'versement', payment_method: 'virement', notes: '' });
  const [depenseForm, setDepenseForm] = useState({ type: 'faux_frais', description: '', montant: '', date: '' });

  useEffect(() => {
    loadEvenement();
    loadAllPrestataires();
    const today = new Date().toISOString().split('T')[0];
    setPaiementForm(prev => ({ ...prev, date: today }));
    setDepenseForm(prev => ({ ...prev, date: today }));
  }, [id]);

  const loadEvenement = async () => {
    try {
      const res = await axios.get(`${API}/evenements/${id}`, { withCredentials: true });
      const eventData = res.data;
      setEvenement(eventData);
      setNote(eventData.note || '');
      setRushesDate(eventData.rushes_delivered_at ? new Date(eventData.rushes_delivered_at).toISOString().split('T')[0] : '');
      setHasChanges(false); // Reset changes state on load
      
      // Load roadmap if exists
      if (res.data.roadmap_id) {
        const roadmapRes = await axios.get(`${API}/roadmaps/${res.data.roadmap_id}`, { withCredentials: true });
        setRoadmap(roadmapRes.data.roadmap);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const dataToSave = {
        note: note,
        rushes_delivered_at: rushesDate || null // Send null if date is empty
      };
      await axios.put(`${API}/evenements/${id}/details`, dataToSave, { withCredentials: true });
      toast.success('Modifications enregistrées');
      setHasChanges(false);
      // Also update the main evenement object locally to reflect the changes
      setEvenement(prev => ({ ...prev, ...dataToSave }));
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };


  const loadAllPrestataires = async () => {
    try {
      const res = await axios.get(`${API}/prestataires`, { withCredentials: true });
      setAllPrestataires(res.data);
    } catch (error) {
      console.error('Error loading prestataires:', error);
    }
  };

  const handleAddPaiement = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/evenements/${id}/paiements`, paiementForm, { withCredentials: true });
      toast.success('Paiement ajouté');
      setPaiementModalOpen(false);
      setPaiementForm({ date: '', montant: '', type: 'versement', payment_method: 'virement', notes: '' });
      loadEvenement();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleAddDepense = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/evenements/${id}/depenses`, depenseForm, { withCredentials: true });
      toast.success('Dépense ajoutée');
      setDepenseModalOpen(false);
      setDepenseForm({ type: 'faux_frais', description: '', montant: '', date: '' });
      loadEvenement();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleViewReceipt = async (paiementId) => {
    try {
      const response = await axios.get(`${API}/evenements/${id}/paiements/${paiementId}/receipt`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create blob URL and open in new window
      const blob = new Blob([response.data], { type: 'text/html' });
      const blobUrl = window.URL.createObjectURL(blob);
      const newWindow = window.open(blobUrl, '_blank');
      
      // Clean up after window closes
      if (newWindow) {
        newWindow.addEventListener('beforeunload', () => {
          window.URL.revokeObjectURL(blobUrl);
        });
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ouverture du récépissé');
      console.error('Error viewing receipt:', error);
    }
  };

  const handleDownloadReceipt = async (paiementId) => {
    try {
      const response = await axios.get(`${API}/evenements/${id}/paiements/${paiementId}/receipt/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Generate filename
      const clientInfo = evenement?.client_info ?? {};
      const clientName = ((clientInfo.prenom || '') + ' ' + (clientInfo.nom || '')).trim();
      const paiement = evenement?.paiements?.find(p => p.id === paiementId);
      const dateStr = paiement?.date ? new Date(paiement.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      link.download = `Recepisse - ${clientName} - ${dateStr}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      toast.error('Erreur lors du téléchargement du récépissé');
      console.error('Error downloading receipt:', error);
    }
  };

  const handleSendReceipt = async (paiementId) => {
    try {
      await axios.post(`${API}/evenements/${id}/paiements/${paiementId}/send-receipt`, {}, { withCredentials: true });
      toast.success('Récépissé envoyé au client !');
      loadEvenement();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi : ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeletePaiement = async (paiementId) => {
    if (!window.confirm('Supprimer ce paiement ?')) return;
    try {
      await axios.delete(`${API}/evenements/${id}/paiements/${paiementId}`, { withCredentials: true });
      toast.success('Paiement supprimé');
      loadEvenement();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEditDepense = (depense) => {
    setEditingDepense(depense);
    setEditDepenseModalOpen(true);
  };

  const handleUpdateDepense = async (e) => {
    e.preventDefault();
    if (!editingDepense) return;
    
    try {
      await axios.put(
        `${API}/evenements/${id}/depenses/${editingDepense.id}`,
        { montant: editingDepense.montant },
        { withCredentials: true }
      );
      toast.success('Montant mis à jour');
      setEditDepenseModalOpen(false);
      setEditingDepense(null);
      loadEvenement();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteDepense = async (depenseId) => {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    try {
      await axios.delete(`${API}/evenements/${id}/depenses/${depenseId}`, { withCredentials: true });
      toast.success('Dépense supprimée');
      loadEvenement();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleQuickAddFromRoadmap = (prestataireInfo, block, dayNum) => {
    const today = new Date().toISOString().split('T')[0];
    setDepenseForm({
      type: 'prestataire',
      description: `${prestataireInfo.role} - ${prestataireInfo.name} - ${block.title} (Jour ${dayNum})`,
      montant: '',
      date: block.date || today
    });
  };

  const handleQuickAddFromList = (prestataire) => {
    const today = new Date().toISOString().split('T')[0];
    const roles = prestataire.roles && prestataire.roles.length > 0 ? prestataire.roles.join(', ') : 'Prestataire';
    setDepenseForm({
      type: 'prestataire',
      description: `${roles} - ${prestataire.prenom} ${prestataire.nom}`,
      montant: '',
      date: today
    });
  };

  // Group roadmap blocks by date
  const getRoadmapSummary = () => {
    if (!roadmap || !roadmap.blocks) return [];
    
    const dayCounter = {};
    return roadmap.blocks.map(block => {
      const dayNum = dayCounter[block.date] = (dayCounter[block.date] || 0) + 1;
      return { ...block, dayNum };
    });
  };

  if (loading) {
    return <div className="p-8">Chargement...</div>;
  }

  if (!evenement) {
    return <div className="p-8">Événement non trouvé</div>;
  }

  const roadmapSummary = getRoadmapSummary();

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/backoffice/finances">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Link>
          </Button>
          {evenement.devis_id && (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/backoffice/devis/${evenement.devis_id}/edit`}>
                <FileText className="h-4 w-4 mr-2" />
                Voir le devis
              </Link>
            </Button>
          )}
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-stone-800">
            {evenement.event_info?.event_type?.replace('_', ' ')} de {evenement.event_info?.vedettes}
          </h1>
          <p className="text-sm sm:text-base text-stone-600">
            {evenement.client_info?.prenom} {evenement.client_info?.nom}
          </p>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-stone-600">Total TTC</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-bold text-stone-800">{evenement.total_ttc?.toLocaleString()}€</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-stone-600 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-600" />
              Reçu
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-bold text-green-600">{evenement.total_recu?.toLocaleString()}€</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-stone-600 flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-rose-600" />
              Dépenses
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-bold text-rose-600">{evenement.total_depenses?.toLocaleString()}€</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-stone-600">Reste à payer</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-bold text-amber-600">{evenement.reste_a_payer?.toLocaleString()}€</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-stone-600 flex items-center gap-2">
              <Wallet className="h-4 w-4 text-blue-600" />
              En Poche
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xl font-bold text-blue-600">{evenement.reste_en_poche?.toLocaleString()}€</p>
          </CardContent>
        </Card>
      </div>

      {/* Details & Notes */}
      <Card className="mb-6 lg:mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Notebook className="h-5 w-5" />
            Informations Internes
          </CardTitle>
        </CardHeader>
        <form onSubmit={handleDetailsSubmit}>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="note">Notes Internes</Label>
              <Textarea
                id="note"
                value={note}
                onChange={(e) => { setNote(e.target.value); setHasChanges(true); }}
                placeholder="Ajoutez des notes sur l'événement, le client, etc."
                className="min-h-[100px]"
              />
            </div>
            <div>
              <Label htmlFor="rushes_delivered_at">Date de livraison des rushes</Label>
              <Input
                id="rushes_delivered_at"
                type="date"
                value={rushesDate}
                onChange={(e) => { setRushesDate(e.target.value); setHasChanges(true); }}
              />
            </div>
            {hasChanges && (
              <Button type="submit" disabled={isSaving}>
                {isSaving ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </Button>
            )}
          </CardContent>
        </form>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Paiements */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Paiements Reçus
              </CardTitle>
              <Dialog open={paiementModalOpen} onOpenChange={setPaiementModalOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-green-600 hover:bg-green-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Ajouter un paiement</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddPaiement} className="space-y-4">
                    <div>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={paiementForm.date}
                        onChange={(e) => setPaiementForm({ ...paiementForm, date: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Montant (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={paiementForm.montant}
                        onChange={(e) => setPaiementForm({ ...paiementForm, montant: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Type</Label>
                      <Select value={paiementForm.type} onValueChange={(val) => setPaiementForm({ ...paiementForm, type: val })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="acompte">Acompte</SelectItem>
                          <SelectItem value="versement">Versement</SelectItem>
                          <SelectItem value="solde">Solde</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Méthode de paiement</Label>
                      <Select value={paiementForm.payment_method} onValueChange={(val) => setPaiementForm({ ...paiementForm, payment_method: val })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cheque">Chèque</SelectItem>
                          <SelectItem value="virement">Virement</SelectItem>
                          <SelectItem value="especes">Espèces</SelectItem>
                          <SelectItem value="carte">Carte bancaire</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Notes (optionnel)</Label>
                      <Input
                        value={paiementForm.notes}
                        onChange={(e) => setPaiementForm({ ...paiementForm, notes: e.target.value })}
                      />
                    </div>
                    <Button type="submit" className="w-full bg-green-600 hover:bg-green-700">Ajouter</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(!evenement.paiements || evenement.paiements.length === 0) ? (
                <p className="text-sm text-stone-500 italic">Aucun paiement enregistré</p>
              ) : (
                evenement.paiements.map((p) => (
                  <div key={p.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 border rounded-lg hover:bg-stone-50">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium text-green-600">{p.montant}€</div>
                        {p.email_sent && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded flex items-center gap-1">
                            ✓ Email envoyé
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-stone-500">
                        {p.date} • {p.type}
                        {p.payment_method && (
                          <span className="ml-1">
                            • {p.payment_method === 'cheque' ? 'Chèque' : 
                               p.payment_method === 'virement' ? 'Virement' : 
                               p.payment_method === 'especes' ? 'Espèces' : 
                               p.payment_method === 'carte' ? 'Carte bancaire' : 
                               p.payment_method}
                          </span>
                        )}
                      </div>
                      {p.notes && <div className="text-xs text-stone-600">{p.notes}</div>}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {/* Bouton Voir le récépissé */}
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleViewReceipt(p.id)}
                        title="Voir le récépissé"
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      
                      {/* Bouton Télécharger PDF */}
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDownloadReceipt(p.id)}
                        title="Télécharger le récépissé (PDF)"
                        className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      
                      {/* Bouton Envoyer/Renvoyer récépissé par email */}
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleSendReceipt(p.id)}
                        title={p.email_sent ? "Renvoyer le récépissé par email" : "Envoyer le récépissé par email"}
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                      
                      {/* Bouton Supprimer */}
                      <Button variant="ghost" size="sm" onClick={() => handleDeletePaiement(p.id)} title="Supprimer le paiement">
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Dépenses avec récapitulatif roadmap */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Dépenses
              </CardTitle>
              <Dialog open={depenseModalOpen} onOpenChange={setDepenseModalOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-rose-600 hover:bg-rose-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Ajouter une dépense</DialogTitle>
                  </DialogHeader>
                  
                  {/* Récapitulatif Roadmap */}
                  {roadmapSummary.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Users className="h-4 w-4 text-amber-600" />
                        <h3 className="font-semibold text-stone-800">Prestataires de la feuille de route</h3>
                      </div>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {roadmapSummary.map((block, idx) => (
                          <div key={idx} className="border rounded-lg p-3 bg-stone-50">
                            <div className="flex items-center gap-2 mb-2">
                              <Calendar className="h-3 w-3 text-stone-500" />
                              <span className="text-xs font-medium text-stone-600">
                                Jour {block.dayNum} - {block.title}
                              </span>
                              <Badge variant="outline" className="text-xs">{block.date}</Badge>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {block.prestataires && block.prestataires.length > 0 ? (
                                block.prestataires.map((p, pidx) => (
                                  <Button
                                    key={pidx}
                                    variant="outline"
                                    size="sm"
                                    className="text-xs h-7"
                                    onClick={() => handleQuickAddFromRoadmap(p, block, block.dayNum)}
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    {p.role} - {p.name}
                                  </Button>
                                ))
                              ) : (
                                <span className="text-xs text-stone-400 italic">Aucun prestataire assigné</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                        💡 Cliquez sur un prestataire ci-dessus pour pré-remplir le formulaire
                      </div>
                    </div>
                  )}

                  {/* Liste complète des prestataires */}
                  {allPrestataires.length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Users className="h-4 w-4 text-blue-600" />
                        <h3 className="font-semibold text-stone-800">Tous les prestataires disponibles</h3>
                      </div>
                      <div className="border rounded-lg p-3 bg-stone-50 max-h-40 overflow-y-auto">
                        <div className="flex flex-wrap gap-2">
                          {allPrestataires.map((presta) => (
                            <Button
                              key={presta.id}
                              variant="outline"
                              size="sm"
                              className="text-xs h-7"
                              onClick={() => handleQuickAddFromList(presta)}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              {presta.prenom} {presta.nom}
                              {presta.roles && presta.roles.length > 0 && (
                                <span className="ml-1 text-stone-400">({presta.roles[0]})</span>
                              )}
                            </Button>
                          ))}
                        </div>
                      </div>
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800">
                        💡 Cliquez pour ajouter un prestataire qui n&apos;est pas sur la roadmap
                      </div>
                    </div>
                  )}

                  {/* Formulaire manuel */}
                  <form onSubmit={handleAddDepense} className="space-y-4">
                    <div>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={depenseForm.date}
                        onChange={(e) => setDepenseForm({ ...depenseForm, date: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Type</Label>
                      <Select value={depenseForm.type} onValueChange={(val) => setDepenseForm({ ...depenseForm, type: val })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="faux_frais">Faux frais</SelectItem>
                          <SelectItem value="prestataire">Prestataire</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input
                        value={depenseForm.description}
                        onChange={(e) => setDepenseForm({ ...depenseForm, description: e.target.value })}
                        placeholder="Ex: Clés USB, Monteur vidéo..."
                        required
                      />
                    </div>
                    <div>
                      <Label>Montant (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={depenseForm.montant}
                        onChange={(e) => setDepenseForm({ ...depenseForm, montant: e.target.value })}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full bg-rose-600 hover:bg-rose-700">Ajouter</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(!evenement.depenses || evenement.depenses.length === 0) ? (
                <p className="text-sm text-stone-500 italic">Aucune dépense enregistrée</p>
              ) : (
                evenement.depenses.map((d) => (
                  <div key={d.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-stone-50 cursor-pointer" onClick={() => handleEditDepense(d)}>
                    <div className="flex-1">
                      <div className="font-medium text-rose-600">-{d.montant}€</div>
                      <div className="text-xs text-stone-500">{d.date} • {d.type}</div>
                      <div className="text-xs text-stone-600">{d.description}</div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteDepense(d.id); }}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Badge */}
      <div className="mt-8 p-4 bg-stone-50 border rounded-lg">
        <p className="text-sm text-stone-600 mb-2">Statut financier :</p>
        <Badge className={
          evenement.statut_financier === 'solde' ? 'bg-green-100 text-green-700 text-lg' :
          evenement.statut_financier === 'partiellement_paye' ? 'bg-amber-100 text-amber-700 text-lg' :
          evenement.statut_financier === 'acompte_recu' ? 'bg-blue-100 text-blue-700 text-lg' :
          'bg-stone-100 text-stone-700 text-lg'
        }>
          {evenement.statut_financier === 'solde' ? '✅ Soldé' :
           evenement.statut_financier === 'partiellement_paye' ? '📊 Partiellement payé' :
           evenement.statut_financier === 'acompte_recu' ? '💰 Acompte reçu' :
           '⏳ En attente'}
        </Badge>
      </div>

      {/* Edit Depense Modal */}
      <Dialog open={editDepenseModalOpen} onOpenChange={setEditDepenseModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier le montant</DialogTitle>
          </DialogHeader>
          {editingDepense && (
            <form onSubmit={handleUpdateDepense} className="space-y-4">
              <div>
                <Label>Description</Label>
                <p className="text-sm text-stone-600 p-2 bg-stone-50 rounded">{editingDepense.description}</p>
              </div>
              <div>
                <Label>Montant (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editingDepense.montant}
                  onChange={(e) => setEditingDepense({ ...editingDepense, montant: parseFloat(e.target.value) || 0 })}
                  required
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setEditDepenseModalOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700">
                  Enregistrer
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EvenementDetail;
